import { Badge } from "../../primitives/badge";
import { Button } from "../../primitives/button";
import {
  X,
  ShoppingCart,
  Calendar,
  TrendingUp,
  ArrowRight,
} from "lucide-react";
import type { JobViewItem } from "@shared-core/data/api-types";
import { formatDateOrDash } from "@shared-core/domain/formatters";
import { Link } from "react-router";
import { paths } from "@shared-core/routing/routes";

interface JobDetailModalProps {
  job: JobViewItem;
  onClose: () => void;
}

export function JobDetailModal({ job, onClose }: JobDetailModalProps) {
  const isInvoiced = !!job.invoiced_at;
  const margin = job.booked_value
    ? ((job.contribution_amt / job.booked_value) * 100).toFixed(1)
    : "0.0";

  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4"
      onClick={onClose}
    >
      <div
        className="bg-background border border-border rounded-lg shadow-lg w-full max-w-2xl max-h-[90vh] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="shrink-0 bg-background border-b border-border px-6 py-4 flex items-start justify-between gap-4">
          <div className="flex items-start gap-3 flex-1">
            <ShoppingCart className="size-6 text-primary shrink-0 mt-0.5" />
            <div className="flex-1">
              <h2 className="text-lg mb-1">Job Details</h2>
              <div className="flex items-center gap-2 flex-wrap">
                <Badge variant={isInvoiced ? "success" : "warning"}>
                  {isInvoiced ? "Invoiced" : "Work in progress"}
                </Badge>
                <Badge variant="outline" className="text-xs">
                  {job.job_no}
                </Badge>
              </div>
            </div>
          </div>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="size-4" />
          </Button>
        </div>

        {/* Content */}
        <div className="flex-1 min-h-0 overflow-y-auto p-6 space-y-5">
          {/* Description */}
          <div>
            <label className="text-sm text-muted-foreground block mb-1">
              Description
            </label>
            <p className="text-sm">{job.description}</p>
          </div>

          {/* Customer */}
          {job.customer && (
            <div>
              <label className="text-sm text-muted-foreground block mb-1">
                Customer
              </label>
              <Link
                to={paths.customerDetail({ customerId: job.customer.id })}
                onClick={onClose}
                className="inline-flex items-center gap-1.5 text-sm font-medium text-primary hover:underline"
              >
                {job.customer.name}
                <ArrowRight className="size-3.5" />
              </Link>
            </div>
          )}

          {/* Financial Summary */}
          <div className="bg-muted/50 rounded-lg p-4 space-y-3">
            <h4 className="text-sm font-medium flex items-center gap-1.5">
              <TrendingUp className="size-4" />
              Financial Summary
            </h4>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="text-sm text-muted-foreground block mb-1">
                  Booked Value
                </label>
                <p className="text-xl font-semibold">
                  £
                  {job.booked_value.toLocaleString("en-GB", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
              </div>
              <div>
                <label className="text-sm text-muted-foreground block mb-1">
                  Contribution
                </label>
                <p className="text-xl font-semibold">
                  £
                  {job.contribution_amt.toLocaleString("en-GB", {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  })}
                </p>
                <p className="text-xs text-muted-foreground mt-0.5">
                  {margin}% margin
                </p>
              </div>
            </div>
          </div>

          {/* Metadata Grid */}
          <div className="grid grid-cols-2 gap-4">
            {job.ordered_at && (
              <div>
                <label className="text-sm text-muted-foreground block mb-1 flex items-center gap-1">
                  <Calendar className="size-3.5" />
                  Booking Date
                </label>
                <p className="text-sm">{formatDateOrDash(job.ordered_at)}</p>
              </div>
            )}
            {job.due_at && (
              <div>
                <label className="text-sm text-muted-foreground block mb-1 flex items-center gap-1">
                  <Calendar className="size-3.5" />
                  Due Date
                </label>
                <p className="text-sm">{formatDateOrDash(job.due_at)}</p>
              </div>
            )}
            {job.invoiced_at && (
              <div>
                <label className="text-sm text-muted-foreground block mb-1 flex items-center gap-1">
                  <Calendar className="size-3.5" />
                  Invoice Date
                </label>
                <p className="text-sm">{formatDateOrDash(job.invoiced_at)}</p>
              </div>
            )}
          </div>

          {/* Source System */}
          <div>
            <label className="text-sm text-muted-foreground block mb-1">
              Tharstern Job Number
            </label>
            <code className="font-mono text-sm bg-muted px-2 py-1 rounded inline-block">
              {job.job_no}
            </code>
          </div>

          {/* IDs */}
          <div className="border-t pt-4 space-y-3">
            <h4 className="text-sm font-medium">Technical Details</h4>
            <div className="grid grid-cols-2 gap-3 text-xs">
              <div>
                <label className="text-muted-foreground block mb-0.5">
                  Job ID
                </label>
                <code className="font-mono bg-muted px-1.5 py-0.5 rounded">
                  {job.id}
                </code>
              </div>
              <div>
                <label className="text-muted-foreground block mb-0.5">
                  Customer ID
                </label>
                <code className="font-mono bg-muted px-1.5 py-0.5 rounded">
                  {job.customer_id}
                </code>
              </div>
              {job.estimate_id && (
                <div>
                  <label className="text-muted-foreground block mb-0.5">
                    Estimate ID
                  </label>
                  <code className="font-mono bg-muted px-1.5 py-0.5 rounded">
                    {job.estimate_id}
                  </code>
                </div>
              )}
              {job.order_no && (
                <div>
                  <label className="text-muted-foreground block mb-0.5">
                    Order No.
                  </label>
                  <code className="font-mono bg-muted px-1.5 py-0.5 rounded">
                    {job.order_no}
                  </code>
                </div>
              )}
              {job.invoice_nos && job.invoice_nos.length > 0 && (
                <div>
                  <label className="text-muted-foreground block mb-0.5">
                    Invoice No{job.invoice_nos.length > 1 ? "s" : ""}.
                  </label>
                  <div className="flex flex-wrap gap-1">
                    {job.invoice_nos.map((invoiceNo) => (
                      <code
                        key={invoiceNo}
                        className="font-mono bg-muted px-1.5 py-0.5 rounded"
                      >
                        {invoiceNo}
                      </code>
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Footer */}
        <div className="shrink-0 bg-background border-t border-border px-6 py-4 flex justify-end">
          <Button onClick={onClose}>Close</Button>
        </div>
      </div>
    </div>
  );
}
